import React, { useRef } from 'react';
import { Send, Loader2, Sparkles, Paperclip, FileText, X } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

/**
 * Chat input area with file upload support
 */
const ChatInputArea = ({
  input,
  setInput,
  onSubmit,
  isTyping,
  uploadedFile,
  setUploadedFile,
  caseSubCategory,
  isDragging,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFileSelect
}) => {
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-gray-200/50 animate-in slide-in-from-bottom duration-500"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="max-w-4xl mx-auto px-6 py-4">
        {/* Progress indicator */}
        <div className="mb-3 flex items-center justify-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
            <span className="text-xs font-medium text-gray-600">Klienttype</span>
          </div>
          <div className="w-6 h-px bg-gray-300"></div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
            <span className="text-xs font-medium text-gray-600">Sakstype</span>
          </div>
          {caseSubCategory && (
            <>
              <div className="w-6 h-px bg-gray-300"></div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <span className="text-xs font-medium text-gray-600">Underkategori</span>
              </div>
            </>
          )}
          <div className="w-6 h-px bg-gray-300"></div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-medium text-gray-900">Beskriv</span>
          </div>
        </div>

        {/* File chip if file is selected */}
        {uploadedFile && (
          <div className="mb-3 flex justify-center">
            <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
              <FileText className="w-4 h-4 text-gray-900" />
              <span className="text-sm text-gray-800 font-medium">{uploadedFile.name}</span>
              <button
                onClick={() => {
                  setUploadedFile(null);
                  toast.info('Fil fjernet');
                }}
                className="text-gray-500 hover:text-gray-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="flex gap-3">
          <div className={`flex-1 relative ${isDragging ? 'ring-2 ring-indigo-400 rounded-xl' : ''}`}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isDragging ? "Slipp filen her..." : "Beskriv din situasjon med dine egne ord..."}
              className="w-full resize-none border-2 border-gray-300 focus:border-gray-400 rounded-xl pl-5 pr-12 py-4 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all bg-white shadow-sm"
              rows={3}
            />
            
            {/* Paperclip icon button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadedFile !== null}
              className={`absolute right-3 bottom-3 p-2 rounded-lg transition-all ${
                uploadedFile 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
              }`}
              title={uploadedFile ? 'Maks 1 fil' : 'Last opp fil'}
            >
              <Paperclip className="w-5 h-5" />
            </button>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={onFileSelect}
              className="hidden"
            />
            
            {/* Character count */}
            {input.length > 0 && (
              <div className="absolute bottom-3 left-3 text-xs text-gray-400">
                {input.length}
              </div>
            )}
          </div>
          
          <Button
            type="submit"
            disabled={!input.trim() || isTyping}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 self-end px-8 py-4 h-auto rounded-2xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
          >
            {isTyping ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </form>

        <p className="text-xs text-gray-500 mt-2 text-center">
          <Sparkles className="w-3 h-3 inline mr-1" />
          AI-drevet • Trygg • Konfidensiell • Trykk Enter for å sende • Maks 1 vedlegg
        </p>
      </div>
    </div>
  );
};

export default ChatInputArea;
